/**
 * The assistant's own colours: the glass its pearl is made of, the neutral that
 * draws its silhouette, and the two blues its ribbons are drawn in.
 *
 * **Authored here rather than in `colors.ts`, on purpose.** That file holds the
 * leaves the app's colour roles read, at the values the device has drawn since
 * the rebuild; this is the assistant's own family, decided separately. Each of
 * these four leaves is also a role of the same name in `roles.ts`, and this
 * branch is merged onto the scheme in `alluzo-theme.ts`, so a call site still
 * writes one token path (`agent.background.orb.intense`) and never a primitive.
 *
 * **Glass with two blue ribbons.** Since 24 Sep 2026 the orb's two ribbons are
 * these two blues in every placement, light and dark: on a light page a sky
 * body (`orb.moderate`) with a deep lit edge (`orb.intense`), on a dark one a
 * body between the two with the lit edge lifted toward the glass. The pearl at
 * the centre stays glass. Before that the orb was black and white spending one
 * blue; `design-system-evidence.md` §*The assistant's blue* has what changed
 * and what it costs.
 *
 * **Three of the four do not invert, and that is the point.** A surface flips
 * with the scheme because the light in the room changed; a piece of glass does
 * not. `background.glass` and `background.orb` are the same on a white page and
 * on a black one. Only `border.glass` — the silhouette, which is a contrast and
 * not an emission — answers the page it stands on.
 *
 * What may spend these: the orb in every placement, its ribbons, the pearl and
 * the floating orb's working glow — never the sheet, a button, the menu, a
 * band, a bubble or the dock's row. `docs/design-system/foundations/colour.md`
 * §*The assistant's blue* names them and `colour.enforcement.test.ts` holds it.
 */
import { palette } from './palette';

export type AgentColors = Readonly<{
  background: Readonly<{
    /**
     * `intense` is the deep blue: the ribbons' lit edge on a light page, the
     * folds, ripples, lens and silhouette. `moderate` is the sky: the ribbons'
     * body on a light page, their lit edge on a dark one, and the floating
     * orb's working glow.
     */
    orb: Readonly<{ moderate: string; intense: string }>;
    /** The near-white the orb's glass is drawn in. The same in both schemes. */
    glass: Readonly<{ intense: string }>;
  }>;
  border: Readonly<{
    /**
     * The silhouette. Graphite against a white page, a mid grey against a black
     * one: a drawn edge has to answer the room even when the glass does not.
     */
    glass: Readonly<{ moderate: string }>;
  }>;
}>;

/** Emitted light, and the glass it passes through: neither inverts. */
const emitted = {
  orb: { moderate: palette.azure[300], intense: palette.azure[500] },
  glass: { intense: palette.gray[50] },
} as const;

export const agentColors: Readonly<{ onLight: AgentColors; onDark: AgentColors }> = {
  onLight: {
    background: emitted,
    border: { glass: { moderate: palette.gray[900] } },
  },
  onDark: {
    background: emitted,
    border: { glass: { moderate: palette.gray[600] } },
  },
};
